import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { withQuery } from '@/lib/query-params'
import { EMPTY_GUEST } from '@/services/checkin'
import type { GuestAttachments, GuestData, GuestField } from '@/services/checkin'
import { createDocumentCapture, createQrCapture } from '@/services/checkin/capture'
import type { DocumentCapture, QrCapture } from '@/services/checkin/capture'
import { readLocal, writeLocal } from '@/services/local-store'
import type { Stay } from '@/services/reserva'
import type { CaptureMethod, EntryChoice, FlowMode, Persona } from './flow'
import './Checkin.css'
import { holderTraveler } from '@/services/reserva/holder'

/** Lo que se guarda en el dispositivo para retomar el registro ("Paso X de 8 · guardado"). Nunca fotos. */
export interface CheckinDraft {
  mode: FlowMode
  persona: Persona
  guest: GuestData
  phone: string
  method: CaptureMethod | null
  /** Elección al pulsar Registrar: captura del documento o rellenar a mano (D33) */
  entry: EntryChoice | null
  docCountryId: string
  documentTypeId: string
  doubtful: GuestField[]
  /** travelRegistrationId tras guardar el paso 5 (o de la persona que se completa) */
  travelerId: string | null
  documents: { name: string; url: string; type: 'PDF' | 'HTML' }[]
  /** Campos que faltan al completar datos de una persona registrada */
  missingFields: string[]
  signaturePending: boolean
}

/** Datos que no salen de la memoria: imágenes y lectura cruda del SDK (bundle §6: no se vuelven a mostrar). */
export interface CheckinVolatile {
  attachments: GuestAttachments
  rawData: unknown
}

const DRAFT_FIELD = 'checkinDraft'

// Titular (D23): utilidad común en services/reserva/holder.ts; se reexporta para los pasos del check-in.
export { holderTraveler }

/** Borrador inicial de una persona. Titular: se precarga con sus datos si ya está en el parte (D23). */
export function newDraft(stay: Pick<Stay, 'registration'>, persona: Persona, mode: FlowMode = 'register'): CheckinDraft {
  const country = stay.registration.catalogs.documentCountries.find((c) => c.isDefault) ?? stay.registration.catalogs.documentCountries[0]
  const holder = persona === 'holder' ? holderTraveler(stay) : null
  const d = holder?.details
  const guest: GuestData = d
    ? {
        ...EMPTY_GUEST,
        name: d.name,
        surname1: d.surname1,
        surname2: d.surname2,
        email: d.email ?? '',
        identifier: d.identifier ?? '',
        identifierTypeId: d.identifierTypeId ?? '',
        supportNumber: d.supportNumber ?? '',
        bornDate: d.bornDate ?? '',
        sexId: d.sexId ?? '',
        countryId: d.countryId ?? country?.id ?? '',
        provinceId: d.provinceId ?? '',
        municipalityId: d.municipalityId ?? '',
        city: d.city ?? '',
        address: d.address ?? '',
        postalCode: d.postalCode ?? '',
      }
    : { ...EMPTY_GUEST, countryId: country?.id ?? '' }
  return {
    mode,
    persona,
    guest,
    phone: '',
    method: null,
    entry: null,
    docCountryId: country?.id ?? '',
    documentTypeId: country?.documentTypes.find((t) => t.scanType !== 'qr')?.id ?? '',
    doubtful: [],
    travelerId: holder?.id ?? null,
    documents: [],
    missingFields: [],
    signaturePending: false,
  }
}

interface FlowValue {
  stay: Stay
  draft: CheckinDraft
  update: (patch: Partial<CheckinDraft>) => void
  updateGuest: (patch: Partial<GuestData>) => void
  start: (draft: CheckinDraft) => void
  volatile: CheckinVolatile
  setVolatile: (patch: Partial<CheckinVolatile>) => void
  capture: DocumentCapture
  qr: QrCapture
  /** Nodo persistente del SDK: MobbScan pinta su visor aquí y lo necesita vivo entre pasos 3 y 4 */
  sdkHost: HTMLElement
}

const FlowContext = createContext<FlowValue | null>(null)

export function useCheckinFlow(): FlowValue {
  const ctx = useContext(FlowContext)
  if (!ctx) throw new Error('useCheckinFlow debe usarse dentro de CheckinFlow')
  return ctx
}

function FlowProvider({ stay, children }: { stay: Stay; children: ReactNode }) {
  const [draft, setDraft] = useState<CheckinDraft>(() => {
    const saved = readLocal<CheckinDraft>(stay.guestCode, DRAFT_FIELD)
    return saved?.guest ? { ...newDraft(stay, 'holder'), ...saved } : newDraft(stay, stay.registration.holderPending ? 'holder' : 'companion')
  })
  const [volatile, setVolatileState] = useState<CheckinVolatile>({ attachments: {}, rawData: undefined })
  const guestCode = stay.guestCode

  useEffect(() => {
    writeLocal(guestCode, DRAFT_FIELD, draft)
  }, [guestCode, draft])

  const update = useCallback((patch: Partial<CheckinDraft>) => setDraft((d) => ({ ...d, ...patch })), [])
  const updateGuest = useCallback((patch: Partial<GuestData>) => setDraft((d) => ({ ...d, guest: { ...d.guest, ...patch } })), [])
  const start = useCallback((next: CheckinDraft) => {
    setDraft(next)
    setVolatileState({ attachments: {}, rawData: undefined })
  }, [])
  const setVolatile = useCallback((patch: Partial<CheckinVolatile>) => setVolatileState((v) => ({ ...v, ...patch })), [])

  const capture = useMemo(() => createDocumentCapture(guestCode), [guestCode])
  const qr = useMemo(() => createQrCapture(guestCode), [guestCode])
  const sdkHost = useMemo(() => {
    const el = document.createElement('div')
    el.id = 'mobbscan-container'
    el.className = 'checkin__sdk'
    return el
  }, [])
  const parkingRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    if (!sdkHost.parentElement) parkingRef.current?.appendChild(sdkHost)
  })
  useEffect(() => () => capture.stop(), [capture])

  const value = useMemo(() => ({ stay, draft, update, updateGuest, start, volatile, setVolatile, capture, qr, sdkHost }), [stay, draft, update, updateGuest, start, volatile, setVolatile, capture, qr, sdkHost])
  return (
    <FlowContext.Provider value={value}>
      {children}
      <div ref={parkingRef} className="checkin__sdk-parking" aria-hidden="true" />
    </FlowContext.Provider>
  )
}

/** Hueco donde se muestra el visor persistente del SDK (dentro de CameraViewer). */
export function SdkSlot({ mode = 'document' }: { mode?: 'document' | 'face' }) {
  const { sdkHost } = useCheckinFlow()
  const ref = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const slot = ref.current
    const parking = document.querySelector('.checkin__sdk-parking')
    slot?.appendChild(sdkHost)
    return () => {
      parking?.appendChild(sdkHost)
    }
  }, [sdkHost])
  return <div ref={ref} className={`checkin__sdk-slot checkin__sdk-slot--${mode}`} />
}

/** Cada paso empieza arriba: un objetivo por pantalla con su título visible. */
function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    if (typeof window.scrollTo === 'function' && !navigator.userAgent.includes('jsdom')) window.scrollTo(0, 0)
  }, [pathname])
  return null
}

/** Rutas de /checkin: sin registro automático en la propiedad no hay flujo (app actual: Checkin.tsx). */
export function CheckinFlow() {
  const stay = useLoadedStay()
  if (!stay.flags.autoCheckIn) return <Navigate to={withQuery('/', window.location.search)} replace />
  return (
    <FlowProvider stay={stay}>
      <ScrollToTop />
      <Outlet />
    </FlowProvider>
  )
}
